import { timeSliceInSeconds } from "web/hooks/audio/useAudioRecorder";

class DataPackageBuilder {
    private startTime: bigint;
    private endTime: bigint;
    private channelContents: number[];
    private data: ByteArrayDeque;

    constructor(startTime: bigint, endTime: bigint, channelContents: number[]) {
        this.startTime = startTime;
        this.endTime = endTime;
        this.channelContents = channelContents;
        this.data = new ByteArrayDeque();
    }

    addSample(sample: number): void {
        const value = new Int16Array([sample]);
        this.data.putBytes(new Uint8Array(value.buffer));
    }

    build(): ArrayBuffer {
        const header = this.getHeader();
        const preHeader = this.genPreHeader(header.byteLength);

        const totalLength = preHeader.byteLength + header.byteLength + this.data.byteLength;
        const packageArray = new Uint8Array(totalLength);

        let offset = 0;
        packageArray.set(new Uint8Array(preHeader), offset);
        offset += preHeader.byteLength;
        packageArray.set(new Uint8Array(header), offset);
        offset += header.byteLength;
        packageArray.set(new Uint8Array(this.data.toByteArray()), offset);

        return packageArray.buffer;
    }

    private genPreHeader(headerLength: number): ArrayBuffer {
        const preHeader = new ByteArrayDeque();
        preHeader.putBytes(new Uint8Array(new Uint16Array([2]).buffer));
        preHeader.putBytes(new Uint8Array(new Uint16Array([headerLength + 4]).buffer));
        return preHeader.toByteArray();
    }

    private getHeader(): ArrayBuffer {
        const header = new ByteArrayDeque();
        header.putBytes(new Uint8Array(new Uint16Array([this.channelContents.length]).buffer));

        header.putBytes(new Uint8Array(new BigUint64Array([this.startTime]).buffer));
        header.putBytes(new Uint8Array(new BigUint64Array([this.endTime]).buffer));

        this.channelContents.forEach(channel => {
            header.putBytes(new Uint8Array([12])); // Fixed value
            header.putBytes(new Uint8Array(new Uint16Array([channel]).buffer)); // Assuming channel is a number
            header.putBytes(new Uint8Array(new Float32Array([0]).buffer)); // offset
            header.putBytes(new Uint8Array(new Float32Array([1]).buffer)); // scale
        });

        return header.toByteArray();
    }
}

class ByteArrayDeque {
    private data: Uint8Array;
    private length: number;

    constructor() {
        this.data = new Uint8Array(1024 * 20 * timeSliceInSeconds); // every second is about 16K
        this.length = 0;
    }

    putBytes(value: Uint8Array): void {
        this.ensureCapacity(this.length + value.byteLength);
        this.data.set(value, this.length);
        this.length += value.byteLength;
    }

    toByteArray(): ArrayBuffer {
        return this.data.buffer.slice(0, this.length);
    }

    flush(): void {
        this.length = 0;
    }

    private ensureCapacity(requiredCapacity: number): void {
        if (requiredCapacity > this.data.byteLength) {
            let newCapacity = this.data.byteLength;
            while (newCapacity < requiredCapacity) {
                newCapacity *= 2; // Double the buffer size
            }
            const newData = new Uint8Array(newCapacity);
            newData.set(this.data, 0);
            this.data = newData;
        }
    }

    get byteLength(): number {
        return this.length;
    }
}

export { DataPackageBuilder, ByteArrayDeque };
